import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { differenceInDays, getDate, getHours, getMonth, getYear, sub } from 'date-fns';
import * as moment from 'moment';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { Frac } from '~lmo/models/frac.model';
import { LmoOrdersService } from '~lmo/services';
import { LmoFracsService } from '~lmo/services/lmo-fracs.service';
import { UserService } from '~services/user.service';

interface NavLink {
  path: (string | number)[];
  label: string;
  line1?: string;
  line2?: string;
  disabled?: boolean;
  warning?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-detail',
  styleUrls: ['./lmo-frac-detail.component.scss'],
  templateUrl: './lmo-frac-detail.component.html',
})
export class LmoFracDetailComponent implements OnInit {
  @ViewChild('bulkBolImageExport', { static: true }) private bulkBolImageExportModal: TemplateRef<any>;
  public optedInToNewBillingPortal = true;
  @HostBinding('class') public className = 'flex-1 overflow-auto';
  private downloadInProgress = false;
  public frac$: Observable<Frac>;
  public navLinks$: Observable<NavLink[]>;
  public bolImageExportStartDateFC = new FormControl(sub(new Date(), { weeks: 1, days: 1 }), [Validators.required]);
  public bolImageExportEndDateFC = new FormControl(sub(new Date(), { days: 2 }), [Validators.required]);
  public validRange$: Observable<boolean>;
  public bolExportOkay: boolean;

  constructor(
    private fracService: LmoFracsService,
    private orderService: LmoOrdersService,
    private datePipe: DatePipe,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar,
    private userService: UserService,
  ) {}

  public ngOnInit() {
    this.frac$ = this.fracService.currentFrac$;
    this.bolExportOkay =
      this.userService.isSandRev() || this.userService.isNexTierAccount() || this.userService.isHalAccount();
    this.navLinks$ = combineLatest([this.frac$, this.orderService.inProgress$, this.orderService.pendingOrders$]).pipe(
      map(([frac, inProgressOrders, pending]) => {
        const links: NavLink[] = [
          {
            label: 'Home',
            path: ['home'],
          },
          {
            label: 'Orders',
            line1: `${pending.length} Pending`,
            line2: `${inProgressOrders.length} In-Progress`,
            path: ['orders'],
          },
          {
            label: 'Stages',
            line1: frac && frac.currentStage ? `${frac.currentStage} / ${frac.totalStages}` : null,
            path: ['stages'],
            warning: this.doesNeedStageUpdate(frac),
          },
        ];
        return links;
      }),
    );
    this.setupValidRange();
  }

  public downloadRecords() {
    if (!this.downloadInProgress) {
      this.downloadInProgress = true;
      this.fracService.getRecordsForCurrentFrac$().subscribe(
        (url) => {
          if (url) {
            window.open(url, '_blank');
            this.downloadInProgress = false;
          }
        },
        (error) => {
          console.log('Error downloading the file.', error);
          this.downloadInProgress = false;
        },
      );
    }
  }

  public startBolExport() {
    this.matDialog.open(this.bulkBolImageExportModal);
  }

  public doBolExport() {
    if (this.bolImageExportStartDateFC.invalid || this.bolImageExportEndDateFC.invalid) {
      return;
    }
    const startYear = getYear(this.bolImageExportStartDateFC.value);
    const startMonth = getMonth(this.bolImageExportStartDateFC.value) + 1;
    const startDay = getDate(this.bolImageExportStartDateFC.value);
    const endYear = getYear(this.bolImageExportEndDateFC.value);
    const endMonth = getMonth(this.bolImageExportEndDateFC.value) + 1;
    const endDay = getDate(this.bolImageExportEndDateFC.value);
    this.fracService
      .startBolImageExportForCurrentFrac$(startYear, startMonth, startDay, endYear, endMonth, endDay)
      .subscribe(
        (message) => {
          this.snackBar.open(message, null, {
            duration: 2500,
          });
        },
        (err) => {
          this.snackBar.open('Something Went Wrong', null, {
            duration: 2500,
            panelClass: ['snackbar-error'],
          });
        },
      );

    console.log(this.bolImageExportStartDateFC.value, this.bolImageExportEndDateFC.value);
    console.log(getHours(this.bolImageExportStartDateFC.value), getHours(this.bolImageExportEndDateFC.value));
  }

  public trackByLabel(_index: number, nav: NavLink): string {
    return nav.label;
  }

  public doesNeedStageUpdate(frac: Frac): string {
    if (frac?.currentStage === 1 || !frac?.fracStages) {
      return null;
    }
    const [mostRecentStage, currentStage] = frac.fracStages.filter(
      (stage) => stage.stageNumber === frac.currentStage || stage.stageNumber === frac.currentStage - 1,
    );
    if (!mostRecentStage || !currentStage) {
      return null;
    }
    const mostRecentStageCompletedTime = moment(mostRecentStage.completedTimestamp);
    const expectedCurrentCompletionTimeWithBuffer = mostRecentStageCompletedTime
      .clone()
      .add(currentStage.pumpTime, 'minutes')
      .add(mostRecentStage.downTime)
      .add(2, 'hours');
    if (moment().isAfter(expectedCurrentCompletionTimeWithBuffer)) {
      return `Last stage update ${this.datePipe.transform(mostRecentStage.completedTimestamp, 'short')}`;
    }
    return null;
  }

  private setupValidRange() {
    this.validRange$ = combineLatest([
      this.bolImageExportStartDateFC.valueChanges.pipe(startWith(this.bolImageExportStartDateFC.value as Date)),
      this.bolImageExportEndDateFC.valueChanges.pipe(startWith(this.bolImageExportEndDateFC.value as Date)),
    ]).pipe(
      map(([startDate, endDate]: [Date, Date]) => {
        if (!startDate || !endDate) {
          return false;
        }
        return differenceInDays(endDate, startDate) <= 6;
      }),
      shareReplay(1),
    );
  }
}
